/**
 * Track carousel: bullets, translateX, RTL, drag to scroll.
 * Root [data-track-carousel]. Children: [data-track-viewport], [data-track-track],
 * [data-track-slide], [data-track-bullets]. Bullets: .track-carousel__bullet.
 */
(function () {
  const initialized = new WeakSet();
  const DRAG_THRESHOLD = 8;
  const SNAP_THRESHOLD = 40;

  function gapPx(track) {
    const g = getComputedStyle(track).gap || getComputedStyle(track).columnGap || '12px';
    const n = parseFloat(String(g).replace('px', ''), 10);
    return Number.isFinite(n) ? n : 12;
  }

  function isRtl() {
    // The theme uses a `body.rtl` class instead of dir="rtl"; honor either.
    return (
      document.documentElement.getAttribute('dir') === 'rtl' ||
      (document.body && document.body.classList.contains('rtl'))
    );
  }

  function setup(root) {
    if (!root || initialized.has(root)) return;
    const viewport = root.querySelector('[data-track-viewport]');
    const track = root.querySelector('[data-track-track]');
    const bulletsHost = root.querySelector('[data-track-bullets]');
    const slides = Array.from(root.querySelectorAll('[data-track-slide]'));
    if (!viewport || !track || !bulletsHost || slides.length === 0) return;

    initialized.add(root);

    let index = 0;
    let maxIndex = 0;
    let maxScroll = 0;
    let step = 0;
    /** Logical scroll 0..maxScroll (LTR: more = show further right in track) */
    let scrollPos = 0;

    let dragActive = false;
    let dragMoved = false;
    let dragPointerId = null;
    let dragStartClient = 0;
    let dragStartScroll = 0;
    let suppressClickUntil = 0;

    function metrics() {
      // offsetWidth, not getBoundingClientRect().width: the latter returns the
      // POST-TRANSFORM bounding box, so when slides start the page with the
      // scroll-trigger animation's rotate(5deg) + translate, the measured width
      // is inflated (w*cos + h*sin). On mobile the carousel is usually below
      // the fold at load, so .section-active hasn't fired and the rotation is
      // still applied — bad metrics → over-shifting on snap. offsetWidth
      // ignores transforms and gives the real flex-basis layout width.
      const slideW = slides[0].offsetWidth;
      const gap = gapPx(track);
      // Use the track's own box width — when viewport has padding (alignment to .page-width),
      // viewport.clientWidth would include that padding and over-report the visible area.
      const viewW = track.clientWidth;
      const n = slides.length;
      const totalW = n * slideW + (n - 1) * gap;
      maxScroll = Math.max(0, totalW - viewW);
      step = slideW + gap;
      // ceil so the final bullet always lands at maxScroll — guarantees the last slide
      // is reachable even when the leftover gap is smaller than one step.
      maxIndex = maxScroll <= 1 ? 0 : Math.ceil(maxScroll / step);
      return { slideW, gap };
    }

    function syncIndexFromScroll(startScroll) {
      if (typeof startScroll === 'number' && step > 0) {
        const delta = scrollPos - startScroll;
        const startIndex = Math.round(Math.min(Math.max(0, startScroll), maxScroll) / step);
        if (Math.abs(delta) >= SNAP_THRESHOLD) {
          const stepsMoved = Math.round(delta / step) || (delta > 0 ? 1 : -1);
          index = Math.min(maxIndex, Math.max(0, startIndex + stepsMoved));
        } else {
          index = Math.min(maxIndex, Math.max(0, startIndex));
        }
      } else {
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i <= maxIndex; i++) {
          const target = Math.min(i * step, maxScroll);
          const d = Math.abs(scrollPos - target);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }
        index = best;
      }
      scrollPos = Math.min(index * step, maxScroll);
    }

    function renderBullets() {
      bulletsHost.innerHTML = '';
      for (let i = 0; i <= maxIndex; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'track-carousel__bullet';
        b.setAttribute('aria-label', 'Slide ' + (i + 1));
        if (i === index) b.classList.add('is-active');
        else if (i === index - 1 || i === index + 1) b.classList.add('is-near');
        b.addEventListener('click', function () {
          index = i;
          scrollPos = Math.min(index * step, maxScroll);
          applyTransform(false);
          renderBullets();
        });
        bulletsHost.appendChild(b);
      }
    }

    function applyTransform(dragging) {
      const rtl = isRtl();
      const x = rtl ? scrollPos : -scrollPos;
      track.style.transform = 'translateX(' + x + 'px)';
      track.classList.toggle('is-dragging', !!dragging);
      viewport.classList.toggle('is-dragging', !!dragging);
      root.classList.toggle('is-dragging', !!dragging);
    }

    function update(fromResize) {
      metrics();
      if (fromResize) {
        scrollPos = Math.min(Math.max(0, scrollPos), maxScroll);
        syncIndexFromScroll();
      } else {
        scrollPos = Math.min(index * step, maxScroll);
      }
      applyTransform(false);
      renderBullets();
    }

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      dragActive = true;
      dragMoved = false;
      dragPointerId = e.pointerId;
      dragStartClient = e.clientX;
      dragStartScroll = scrollPos;
      // Pointer capture is deferred until the threshold is crossed. Capturing on
      // pointerdown redirects the synthesized mouseup/click pair to the viewport,
      // which kills clicks on inner <a>/<button> targets (quick-add, card links).
      // (Also: on iOS, setPointerCapture on a partially-offscreen element can
      // cause the page to scroll the element into view — manifests as the page
      // jumping when the user starts a swipe.)
    }

    function onPointerMove(e) {
      if (!dragActive || e.pointerId !== dragPointerId) return;
      const delta = e.clientX - dragStartClient;
      if (!dragMoved) {
        if (Math.abs(delta) <= DRAG_THRESHOLD) return;
        dragMoved = true;
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      e.preventDefault();
      const dx = isRtl() ? delta : -delta;
      scrollPos = Math.min(maxScroll, Math.max(0, dragStartScroll + dx));
      applyTransform(true);
    }

    function endDrag(e) {
      if (!dragActive) return;
      if (e && e.pointerId !== undefined && dragPointerId !== null && e.pointerId !== dragPointerId) return;
      const pid = dragPointerId != null ? dragPointerId : e && e.pointerId;
      const wasMoved = dragMoved;
      dragActive = false;
      dragMoved = false;
      dragPointerId = null;
      if (pid != null) {
        try {
          viewport.releasePointerCapture(pid);
        } catch (err2) {}
      }
      if (wasMoved) {
        // Block the synthetic click that follows a real drag so an in-flight gesture
        // doesn't navigate via a child <a>.
        suppressClickUntil = Date.now() + 300;
      }
      syncIndexFromScroll(dragStartScroll);
      applyTransform(false);
      renderBullets();
    }

    // Mouse / non-touch pointers go through the pointer-event path.
    function onPointerDownMouse(e) {
      if (e.pointerType === 'touch') return;
      onPointerDown(e);
    }
    function onPointerMoveMouse(e) {
      if (e.pointerType === 'touch') return;
      onPointerMove(e);
    }
    function onPointerUpMouse(e) {
      if (e && e.pointerType === 'touch') return;
      endDrag(e);
    }
    viewport.addEventListener('pointerdown', onPointerDownMouse);
    viewport.addEventListener('pointermove', onPointerMoveMouse, { passive: false });
    viewport.addEventListener('pointerup', onPointerUpMouse);
    viewport.addEventListener('pointercancel', onPointerUpMouse);
    viewport.addEventListener('lostpointercapture', function () {
      endDrag(null);
    });

    // Touch goes through native touch events. Pointer events on iOS Safari
    // are unreliable for horizontal carousels: with `touch-action: pan-y`
    // the browser's gesture engine often cancels them mid-swipe (causing
    // "drag refuses to engage"), and the brief pre-emptive vertical pan
    // before cancellation can leave the page at a different scroll pos
    // (causing the page to "jump upward" on long pages — most visible on
    // product templates). Touch events let us call preventDefault from the
    // very first touchmove that's clearly horizontal, which keeps iOS from
    // engaging any native scroll at all.
    let touchActive = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartScroll = 0;
    let touchMoved = false;
    let touchDir = null; // 'h' once we're sure it's horizontal

    viewport.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        touchActive = true;
        touchMoved = false;
        touchDir = null;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartScroll = scrollPos;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchmove',
      function (e) {
        if (!touchActive || !e.touches || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;

        if (!touchDir) {
          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);
          // Detect direction at 3 px so we call e.preventDefault() before iOS
          // commits to page scroll. With touch-action removed from the viewport
          // CSS, the browser waits for our preventDefault signal rather than
          // pre-committing to vertical pan on touchstart.
          if (absDx < 3 && absDy < 3) return;
          // Tie (pure diagonal) goes to horizontal — we'd rather block scroll
          // briefly than let a horizontal swipe turn into page scroll.
          touchDir = absDx >= absDy ? 'h' : 'v';
          if (touchDir === 'v') {
            // Vertical swipe — let the page scroll normally.
            touchActive = false;
            return;
          }
        }

        // Block page scroll as soon as horizontal direction is confirmed (3 px),
        // even before we start moving the carousel visually (DRAG_THRESHOLD).
        if (e.cancelable) e.preventDefault();
        // Hold off on visual drag until DRAG_THRESHOLD so tiny taps don't
        // nudge the carousel unexpectedly.
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        if (!touchMoved) {
          touchMoved = true;
          viewport.classList.add('is-dragging');
          root.classList.add('is-dragging');
          track.classList.add('is-dragging');
        }
        const ddx = isRtl() ? dx : -dx;
        scrollPos = Math.min(maxScroll, Math.max(0, touchStartScroll + ddx));
        const x = isRtl() ? scrollPos : -scrollPos;
        track.style.transform = 'translateX(' + x + 'px)';
      },
      { passive: false }
    );

    function endTouch() {
      if (!touchActive) return;
      const wasMoved = touchMoved;
      touchActive = false;
      touchMoved = false;
      touchDir = null;
      viewport.classList.remove('is-dragging');
      root.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      if (wasMoved) {
        suppressClickUntil = Date.now() + 300;
        syncIndexFromScroll(touchStartScroll);
        applyTransform(false);
        renderBullets();
      }
    }
    viewport.addEventListener('touchend', endTouch);
    viewport.addEventListener('touchcancel', endTouch);

    // Native HTML drag (the ghost image when grabbing an <img> or <a>) blocks pointer
    // events on those targets — kill it.
    viewport.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // Capture-phase so we win against the link's own click handler.
    viewport.addEventListener(
      'click',
      function (e) {
        if (Date.now() < suppressClickUntil) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    const ro = new ResizeObserver(function () {
      update(true);
    });
    ro.observe(viewport);

    update(false);
  }

  function mountAll() {
    document.querySelectorAll('[data-track-carousel]').forEach(function (root) {
      if (!initialized.has(root)) setup(root);
    });
  }

  window.initThemeTrackCarousel = setup;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', function () {
    requestAnimationFrame(mountAll);
  });
})();